import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Link,
  Select,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { AgeRangeSelect } from '../../../components/age-range-select';
import { usePermissions } from '../../../hooks/usePermissions';
import { createGrid } from '../../../services/plans/createGrid';
import { updateGrid } from '../../../services/plans/updateGrid';
import {
  InsurancePlan,
  UpdateGridRequest,
} from '../../../types/plans/types';
import {
  coerceRangeForBackend,
  isValidRange,
  parseBackendAgeString,
  rangeToLabel,
} from '../../../utils/ageRanges';
import { PLAN_DETAIL_COPY } from './planDetailCopy';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  planId: string; // insurance id from route
  row: InsurancePlan | null;
  onSaved: (
    updated: {
      credentialstatus: string;
      value: string;
      age: string;
      minimumAge?: number | null;
      maximumAge?: number | null;
    },
    changedFields?: string[]
  ) => void;
};

const CREDENTIAL_STATUS_OPTIONS = ['Pending', 'Credentialed'];

const PATIENT_ACCEPTANCE_OPTIONS = [
  'Established Patients',
  'Schedule Allowed',
  'PCP Match',
  'No Status',
  'Not Participating',
];

// Age ranges now sourced from AgeRangeCatalog SSOT

export default function InsuranceRowDrawer({
  isOpen,
  onClose,
  planId,
  row,
  onSaved,
}: Props) {
  const toast = useToast();
  const { canEditInsurance, isAdmin } = usePermissions();

  const planRow = row?.plans?.[0];

  const parseAge = useMemo(() => {
    // Prioritize numeric fields if available, fallback to parsing age string
    if (
      planRow?.minimumAge !== undefined ||
      planRow?.maximumAge !== undefined
    ) {
      // Use the numeric fields directly if they exist
      return {
        min: planRow?.minimumAge ?? null,
        max: planRow?.maximumAge ?? null,
      };
    } else {
      // Fallback to parsing the age string
      return parseBackendAgeString(planRow?.age);
    }
  }, [planRow?.age, planRow?.minimumAge, planRow?.maximumAge]);

  const [credentialStatus, setCredentialStatus] =
    useState<string>('Pending');
  const [accepting, setAccepting] = useState<string>('No Status');
  const [minAge, setMinAge] = useState<number | null>(parseAge.min);
  const [maxAge, setMaxAge] = useState<number | null>(parseAge.max);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [staleWriteWarning, setStaleWriteWarning] = useState(false);
  const [originalData, setOriginalData] = useState<{
    credentialstatus: string;
    value: string;
    age: string;
  } | null>(null);

  // Check if user can edit
  const canEdit = canEditInsurance() || isAdmin();

  // Check if plan is expired
  const isExpired = useMemo(() => {
    if (!planRow?.insurance?.endDate) return false;
    return new Date(planRow.insurance.endDate) < new Date();
  }, [planRow?.insurance?.endDate]);

  useEffect(() => {
    const next = row?.plans?.[0];
    setCredentialStatus(next?.credentialstatus || 'Pending');
    setAccepting(next?.value || 'No Status');
    const parsed = parseAge;
    setMinAge(parsed.min);
    setMaxAge(parsed.max);
    setError(null);
    setStaleWriteWarning(false);

    // Store original data for stale write detection
    if (next) {
      setOriginalData({
        credentialstatus: next.credentialstatus,
        value: next.value,
        age: next.age || '',
        // version: next.version || 1, // Version field doesn't exist in Plan type
      });
    }
  }, [row, parseAge]);

  const isValid = useMemo(() => {
    if (!row) return false;
    if (!credentialStatus || !accepting) return false;
    return isValidRange({ min: minAge, max: maxAge });
  }, [row, credentialStatus, accepting, minAge, maxAge]);

  const handleSave = async () => {
    if (!row || !planRow) return;

    // Check permissions
    if (!canEdit) {
      toast({
        title: 'Permission Denied',
        description:
          'You do not have permission to edit insurance plans.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if plan is expired
    if (isExpired) {
      toast({
        title: 'Cannot Edit Expired Plan',
        description: 'This plan has expired and cannot be edited.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!isValid) {
      setError(
        'Please complete all required fields with valid values.'
      );
      return;
    }

    // Check for stale write
    if (originalData) {
      setStaleWriteWarning(true);
      toast({
        title: 'Data May Be Outdated',
        description:
          'This plan may have been modified by another user. Please review the latest changes.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const { minimumAge, maximumAge } = coerceRangeForBackend({
      min: minAge ?? null,
      max: maxAge ?? null,
    });

    const payload: UpdateGridRequest = {
      id: planRow.id,
      practiceId: row.practice.id,
      providerId: row.provider.id,
      plan_id: planId,
      credentialStatus,
      value: accepting,
      minimumAge,
      maximumAge,
    };

    setIsSaving(true);
    setError(null);
    try {
      if (payload.id) {
        await updateGrid(payload);
      } else {
        await createGrid({ gridEntries: [payload] });
      }

      const ageLabel = rangeToLabel({
        min: minimumAge,
        max: maximumAge,
      });

      // Track changed fields
      const changedFields: string[] = [];
      if (planRow.credentialstatus !== credentialStatus) {
        changedFields.push('credentialstatus');
      }
      if (planRow.value !== accepting) {
        changedFields.push('acceptingPatients');
      }
      // Get original age range using same logic as parseAge
      let originalAgeRange;
      if (
        planRow?.minimumAge !== undefined ||
        planRow?.maximumAge !== undefined
      ) {
        originalAgeRange = {
          min: planRow?.minimumAge ?? null,
          max: planRow?.maximumAge ?? null,
        };
      } else {
        originalAgeRange = parseBackendAgeString(planRow.age);
      }
      const originalAge = rangeToLabel(originalAgeRange);
      if (originalAge !== ageLabel) {
        changedFields.push('ageRange');
      }

      onSaved(
        {
          credentialstatus: credentialStatus,
          value: accepting,
          age: ageLabel,
          minimumAge,
          maximumAge,
        },
        changedFields
      );

      toast({
        title: PLAN_DETAIL_COPY.toast.saved,
        description: `Updated ${
          row?.plans?.[0]?.insurance?.friendlyName || 'plan'
        } for ${row?.provider.firstName || ''} ${
          row?.provider.lastName || ''
        }`,
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      onClose();
    } catch (e: unknown) {
      // Handle specific error cases
      const error = e as { response?: { status?: number } };
      if (error?.response?.status === 409) {
        setStaleWriteWarning(true);
        setError(
          'This plan has been modified by another user. Please refresh and try again.'
        );
      } else if (error?.response?.status === 403) {
        setError('You do not have permission to edit this plan.');
      } else {
        setError(
          'Server rejected the update. Please review values and try again.'
        );
      }

      toast({
        title: PLAN_DETAIL_COPY.toast.error,
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const title = row
    ? `Edit ${row.provider.firstName || ''} ${
        row.provider.lastName || ''
      } in ${row.plans?.[0]?.insurance?.friendlyName || 'plan'}`
    : 'Edit provider in plan';

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="md"
      trapFocus
    >
      <DrawerOverlay />
      <DrawerContent
        role="dialog"
        aria-labelledby="drawer-title"
        aria-describedby="drawer-description"
      >
        <DrawerCloseButton />
        <DrawerHeader>
          <Flex direction="column" gap="0.5rem">
            <Text
              id="drawer-title"
              fontSize="1.25rem"
              fontWeight="600"
            >
              {title}
            </Text>
            {row && (
              <Text
                id="drawer-description"
                fontSize="0.875rem"
                color="gray.600"
                fontWeight="400"
              >
                Practice: {row.practice.name || ''},{' '}
                {row.practice.state || ''}
              </Text>
            )}
          </Flex>
        </DrawerHeader>
        <DrawerBody>
          {/* Stale Write Warning */}
          {staleWriteWarning && (
            <Alert status="warning" mb="1rem" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontSize="0.875rem" fontWeight="500">
                  Data May Be Outdated
                </Text>
                <Text fontSize="0.75rem">
                  This plan may have been modified by another user.
                  Please refresh the page to see the latest changes.
                </Text>
              </Box>
            </Alert>
          )}

          {/* Expired Plan Warning */}
          {isExpired && (
            <Alert status="error" mb="1rem" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontSize="0.875rem" fontWeight="500">
                  Plan Expired
                </Text>
                <Text fontSize="0.75rem">
                  This plan has expired and cannot be edited.
                </Text>
              </Box>
            </Alert>
          )}

          {/* Helper Text */}
          <Box
            p="0.75rem"
            bg="blue.50"
            borderRadius="0.375rem"
            border="1px solid"
            borderColor="blue.200"
            mb="1rem"
          >
            <Text
              fontSize="0.8rem"
              fontWeight="500"
              color="blue.700"
              mb="0.25rem"
            >
              Quick Edit
            </Text>
            <Text
              fontSize="0.75rem"
              color="blue.600"
              lineHeight="1.3"
            >
              Edit this provider's insurance plan settings. Changes
              apply immediately to this plan group.
            </Text>
          </Box>

          {row ? (
            <Flex direction="column" gap="1rem">
              <FormControl>
                <FormLabel htmlFor="credential-status">
                  Credential status
                </FormLabel>
                <Text fontSize="0.75rem" color="gray.500" mb="0.5rem">
                  Select the provider's credentialing status for this
                  insurance plan
                </Text>
                <Select
                  id="credential-status"
                  value={credentialStatus}
                  onChange={(e) =>
                    setCredentialStatus(e.target.value)
                  }
                  aria-describedby="credential-status-help"
                  isDisabled={!canEdit || isExpired}
                >
                  {CREDENTIAL_STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="accepting-patients">
                  Accepting patients?
                </FormLabel>
                <Text fontSize="0.75rem" color="gray.500" mb="0.5rem">
                  Indicate whether the provider is accepting new
                  patients for this plan
                </Text>
                <Select
                  id="accepting-patients"
                  value={accepting}
                  onChange={(e) => setAccepting(e.target.value)}
                  aria-describedby="accepting-patients-help"
                  isDisabled={!canEdit || isExpired}
                >
                  {PATIENT_ACCEPTANCE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <AgeRangeSelect
                value={{ min: minAge ?? null, max: maxAge ?? null }}
                onChange={(range) => {
                  setMinAge(range.min);
                  setMaxAge(range.max);
                }}
                label="Age Range"
                helperText="Select the age range for patients accepted under this plan"
                placeholder="Select age range"
                isDisabled={!canEdit || isExpired}
                isRequired
                width="100%"
                data-testid="age-range-select"
              />

              {error ? (
                <Text color="red.500" fontSize="0.875rem">
                  {error}
                </Text>
              ) : null}

              {/* Links section */}
              <Flex
                mt="1rem"
                pt="1rem"
                borderTop="1px solid"
                borderColor="gray.200"
                direction="column"
                gap="0.5rem"
              >
                <Text fontSize="0.875rem" color="gray.600">
                  Need to edit multiple providers?{' '}
                  <Link
                    color="blue.500"
                    textDecoration="underline"
                    onClick={() => {
                      if (row) {
                        window.dispatchEvent(
                          new CustomEvent('go-to-bulk-edit', {
                            detail: {
                              providerId: row.provider.id,
                              practiceId: row.practice.id,
                            },
                          })
                        );
                        onClose();
                      }
                    }}
                  >
                    Open in bulk editor...
                  </Link>
                </Text>
              </Flex>
            </Flex>
          ) : null}
        </DrawerBody>
        <DrawerFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            {canEdit ? 'Cancel' : 'Close'}
          </Button>
          {canEdit && (
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={isSaving}
              isDisabled={!isValid || isExpired || staleWriteWarning}
            >
              Save
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}


